import { UserAvatar } from "components/UserAvatar";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getTreeViewPath } from "routes/Paths";

import { avatarTones } from "../data";

import type { CardAvatar, TreeCard } from "../models";

import LineIcon from "./LineIcon";

import styles from "../HomePage.module.css";

type TreeLibraryProps = {
	cards: TreeCard[];
};

const TreeLibrary = ({ cards }: TreeLibraryProps) => {
	const { t } = useTranslation("homePage");

	return (
		<section aria-label={t("library.label")} className={styles.treeGrid}>
			{cards.map((tree: TreeCard) => (
				<article key={tree.id} className={styles.treeCard}>
					<Link className={styles.treeCardLink} to={getTreeViewPath(tree.id)}>
						<div className={styles.coverFrame}>
							<img alt="" className={styles.coverImage} src={tree.cover} />
							<span className={`${styles.statusBadge} ${styles[`status${tree.status}`]}`}>
								{tree.status}
							</span>
						</div>

						<div className={styles.cardBody}>
							<h2 className={styles.cardTitle}>{tree.title}</h2>
							<p className={styles.cardDescription}>{tree.description}</p>

							<div className={styles.cardMeta}>
								{tree.avatars.length > 0 && (
									<div aria-label={`${tree.title} collaborators`} className={styles.avatarStack}>
										{tree.avatars.map((avatar: CardAvatar, index: number) => (
											<UserAvatar
												key={`${tree.id}-${avatar.name}`}
												className={`${styles.collaborator} ${
													styles[avatarTones[index % avatarTones.length]]
												}`}
												imageUrl={avatar.imageUrl}
												initials={avatar.initials}
												title={avatar.name}
											/>
										))}
									</div>
								)}

								<span
									className={
										tree.isLiked ? `${styles.likeCount} ${styles.likeCountLiked}` : styles.likeCount
									}>
									<LineIcon name="heart" />
									{tree.likes}
								</span>
							</div>

							{/* Entity count then last edited, the same pair in the same order as the explore
							    cards, so a tree reads identically wherever it is listed. */}
							<div className={styles.cardFooter}>
								<span className={styles.entityCount}>
									<LineIcon name="tree" />
									<span>{t("library.entityCount", { count: tree.entityCount })}</span>
								</span>
								<span className={styles.editedAt}>
									<LineIcon name="clock" />
									<span>{t("library.lastEdited", { value: tree.lastEditedLabel })}</span>
								</span>
							</div>
						</div>
					</Link>
				</article>
			))}
		</section>
	);
};

export default TreeLibrary;
